// Shared helpers for the Data → Hierarchy screen.
// Mirrors util/hierarchyValidation.js on the server; the server remains the
// authority — these only drive the UI (dropdown filtering, instant feedback).

export const HIERARCHY_RANK = {
  MENTOR: 0,
  HEAD_MENTOR: 1,
  CLASS_HEAD: 2,
  CHIEF_MENTOR: 3,
  ADMIN: 4,
};

export const HIERARCHY_LEVELS = Object.keys(HIERARCHY_RANK);

export const HIERARCHY_LABEL = {
  MENTOR: 'Mentor',
  HEAD_MENTOR: 'Head Mentor',
  CLASS_HEAD: 'Class Head',
  CHIEF_MENTOR: 'Chief Mentor',
  ADMIN: 'Admin',
};

export const formatMentorName = (mentor) =>
  mentor?.displayName ||
  [mentor?.firstName, mentor?.lastName].filter(Boolean).join(' ') ||
  mentor?.username ||
  `Mentor #${mentor?.id}`;

/** Flatten a nested tree (plus orphans) back into a single lookup list. */
export const flattenTree = (nodes = [], acc = []) => {
  nodes.forEach((node) => {
    acc.push(node);
    if (node.children?.length) flattenTree(node.children, acc);
  });
  return acc;
};

/** Ids of a mentor's whole downline — never valid supervisor choices. */
export const collectDescendantIds = (node, acc = new Set()) => {
  (node?.children || []).forEach((child) => {
    acc.add(child.id);
    collectDescendantIds(child, acc);
  });
  return acc;
};

/**
 * Supervisors an admin may legally pick for `mentor` at `nextHierarchy`:
 * strictly higher rank, not the mentor, and not inside their own downline.
 */
export const eligibleSupervisors = (allMentors, mentor, nextHierarchy) => {
  if (!mentor) return [];
  const nextRank = HIERARCHY_RANK[nextHierarchy ?? mentor.hierarchy];
  const banned = collectDescendantIds(mentor);
  return allMentors
    .filter(
      (candidate) =>
        candidate.id !== mentor.id &&
        !banned.has(candidate.id) &&
        HIERARCHY_RANK[candidate.hierarchy] > nextRank
    )
    .sort((a, b) => formatMentorName(a).localeCompare(formatMentorName(b)));
};

/** Reports that would meet or outrank `mentor` after a demotion. */
export const outrankedReports = (mentor, nextHierarchy) => {
  const nextRank = HIERARCHY_RANK[nextHierarchy];
  return (mentor?.children || []).filter(
    (child) => HIERARCHY_RANK[child.hierarchy] >= nextRank
  );
};
